import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useContracts, useContractActions, CONTRACT_STATUS_LABEL, CONTRACT_STATUS_VARIANT, type ContractRow } from "./api";
import { dateShort } from "@/lib/format";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FILTERS: [string, string][] = [["open", "En cours"], ["signed", "Signés"], ["renewals", "À renouveler"], ["closed", "Refusés / annulés"], ["all", "Tous"]];

export default function ContractsPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState("open");
  const { data: contracts, isLoading, error } = useContracts({ filter });
  const [creating, setCreating] = useState(false);
  const [clientId, setClientId] = useState("");
  const actions = useContractActions();
  const { data: clients } = useQuery({ queryKey: ["clients", "pick"], enabled: creating, queryFn: async () => { const { data, error } = await supabase.from("clients").select("id, legal_name").is("deleted_at", null).eq("status", "active").order("legal_name"); if (error) throw error; return data; } });
  return (
    <div>
      <PageHeader title="Contrats" description="Modèles en français à variables, envoi en signature électronique (Yousign) ou signature hors ligne, avenants, renouvellements." actions={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" />Contrat-cadre</Button>} />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4"><TabsList className="flex-wrap">{FILTERS.map(([k, l]) => <TabsTrigger key={k} value={k}>{l}</TabsTrigger>)}</TabsList></Tabs>
      {error ? <QueryError error={error} /> : isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !contracts?.length ? (
        <EmptyState title={filter === "renewals" ? "Rien à renouveler dans les 30 jours" : "Aucun contrat"} description="Un contrat se crée depuis une commande (variables pré-remplies) ou depuis un client (contrat-cadre)." />
      ) : <ContractsTable contracts={contracts} onOpen={(id) => nav(`/contrats/${id}`)} showClient />}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contrat-cadre</DialogTitle><DialogDescription>Un contrat sans commande : les variables de commande restent vides et devront être renseignées dans les clauses complémentaires. Pour un contrat de commande, passe par la commande.</DialogDescription></DialogHeader>
          <div className="space-y-1.5"><Label htmlFor="ct-client">Client *</Label><Select value={clientId} onValueChange={setClientId}><SelectTrigger id="ct-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger><SelectContent>{clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setCreating(false)}>Annuler</Button><Button disabled={!clientId || actions.create.isPending} onClick={() => actions.create.mutate({ clientId }, { onSuccess: (id) => { setCreating(false); nav(`/contrats/${id}/modifier`); } })}>Créer le brouillon</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ContractsTable({ contracts, onOpen, showClient = false }: { contracts: ContractRow[]; onOpen: (id: string) => void; showClient?: boolean }) {
  return (
    <div className="glass overflow-x-auto rounded-2xl">
      <Table>
        <TableHeader><TableRow><TableHead>N°</TableHead>{showClient && <TableHead>Client</TableHead>}<TableHead>Objet</TableHead><TableHead>Commande</TableHead><TableHead>Signataire</TableHead><TableHead>Fin</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>
          {contracts.map((c) => (
            <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
              <TableCell className="font-mono text-xs">{c.number ?? "brouillon"}{c.kind === "amendment" ? " (avenant)" : ""}</TableCell>
              {showClient && <TableCell className="font-medium">{c.clients?.legal_name}</TableCell>}
              <TableCell>{c.title}{!c.lawyer_validated && c.status !== "draft" && <Badge variant="destructive" className="ml-2">non validé</Badge>}</TableCell>
              <TableCell className="text-muted-foreground">{c.orders?.number ?? "—"}</TableCell>
              <TableCell className="text-muted-foreground">{c.signer_name ?? "—"}</TableCell>
              <TableCell>{c.end_date ? dateShort(c.end_date) : "—"}</TableCell>
              <TableCell><Badge variant={CONTRACT_STATUS_VARIANT[c.status]}>{CONTRACT_STATUS_LABEL[c.status]}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
